import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import { descriptionBullets } from '../../utils/trackingLinks';
import { addIncludedItem, deleteIncludedItem } from '../../utils/includedItems';
import { MONO, SANS } from '../../constants/fonts';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    maxHeight: '80vh',
    background: '#fff',
    border: '1px solid #e5e5ea',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    fontFamily: SANS,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 20px 4px',
  },
  title: {
    fontFamily: MONO,
    fontSize: '1rem',
  },
  close: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    lineHeight: 1,
    color: '#999',
    cursor: 'pointer',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: '#888',
    lineHeight: 1.5,
    margin: '0 20px 12px',
  },
  newRow: {
    display: 'flex',
    gap: 8,
    margin: '0 20px 14px',
  },
  newInput: {
    flex: 1,
    minWidth: 0,
    padding: '9px 10px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.85rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  newButton: {
    padding: '9px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  list: {
    overflowY: 'auto',
    padding: '0 20px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  empty: {
    margin: '0 20px 12px',
    fontSize: '0.85rem',
    color: '#999',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 4px',
    fontSize: '0.9rem',
    color: '#333',
  },
  label: {
    flex: 1,
    cursor: 'pointer',
  },
  checkbox: {
    width: 16,
    height: 16,
    flexShrink: 0,
    cursor: 'pointer',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#bbb',
    fontSize: '1.1rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 2px',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px 20px',
    borderTop: '1px solid #f0f0f0',
  },
  addButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  addButtonDisabled: {
    background: '#ccc',
    cursor: 'default',
  },
  cancelButton: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#222',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
};

export default function IncludedItemsModal({ existingDescription, onApply, onClose }) {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    const q = query(collection(db, 'includedItems'), orderBy('label'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLibrary(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const existingLines = new Set(descriptionBullets(existingDescription).map((l) => l.toLowerCase()));

  const toggle = (label) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleAddToLibrary = async () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    setNewLabel('');
    await addIncludedItem(trimmed);
    // Also select it, so it's ready to apply to this link right away.
    setSelected((s) => new Set(s).add(trimmed));
  };

  const handleRemoveFromLibrary = async (item) => {
    setSelected((s) => {
      if (!s.has(item.label)) return s;
      const next = new Set(s);
      next.delete(item.label);
      return next;
    });
    await deleteIncludedItem(item.id);
  };

  const handleApply = () => {
    if (selected.size === 0) return;
    onApply([...selected]);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>Add included items</div>
          <button type="button" style={styles.close} aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <p style={styles.subtitle}>
          Pick from your saved items, or add a new one to the library below — it'll be here for every link from now on.
        </p>

        <div style={styles.newRow}>
          <input
            style={styles.newInput}
            type="text"
            placeholder="New item, e.g. Home screen"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddToLibrary();
              }
            }}
          />
          <button type="button" style={styles.newButton} onClick={handleAddToLibrary}>
            Save to library
          </button>
        </div>

        <div style={styles.list}>
          {loading && <div style={styles.empty}>Loading…</div>}
          {!loading && library.length === 0 && (
            <div style={styles.empty}>No included items yet — add your first one above.</div>
          )}
          {library.map((item) => {
            const already = existingLines.has(item.label.toLowerCase());
            const checked = selected.has(item.label);
            return (
              <div key={item.id} style={{ ...styles.row, opacity: already ? 0.45 : 1 }}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={checked}
                  disabled={already}
                  onChange={() => toggle(item.label)}
                  id={`included-item-${item.id}`}
                />
                <label htmlFor={`included-item-${item.id}`} style={styles.label}>
                  {item.label}{already ? ' (already added)' : ''}
                </label>
                <button
                  type="button"
                  style={styles.removeButton}
                  onClick={() => handleRemoveFromLibrary(item)}
                  aria-label={`Remove ${item.label} from library`}
                  title="Remove from library"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            style={{ ...styles.addButton, ...(selected.size === 0 ? styles.addButtonDisabled : {}) }}
            onClick={handleApply}
            disabled={selected.size === 0}
          >
            Add to link{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
